import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

export interface ThemeConfig {
  primaryColor: string;
  secondaryColor: string;
}

interface ThemeContextType {
  themeConfig: ThemeConfig;
  currentApplication: string;
  updateTheme: (config: Partial<ThemeConfig>, application?: string) => void;
  resetTheme: (application?: string) => void;
  getAntdTheme: () => any;
}

// Application-specific default colors
const applicationDefaults: Record<string, ThemeConfig> = {
  app: {
    primaryColor: '#13afe3', // ThiranAI brand blue
    secondaryColor: '#6b7280', // Gray-500
  },
  training: {
    primaryColor: '#059669', // Emerald-600 (green)
    secondaryColor: '#6b7280', // Gray-500
  },
  demo: {
    primaryColor: '#ec4899', // Pink-500
    secondaryColor: '#6b7280', // Gray-500
  },
  feedback: {
    primaryColor: '#8b5cf6', // Purple-500 (feedback app)
    secondaryColor: '#6b7280', // Gray-500
  },
  fallbackapp: {
    primaryColor: '#8b5cf6', // Violet-500 (purple)
    secondaryColor: '#6b7280', // Gray-500
  },
  iam: {
    primaryColor: '#7c3aed', // Violet-600 (IAM purple)
    secondaryColor: '#6b7280', // Gray-500
  },
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

// Helper function to get application from URL
const getApplicationFromPath = (pathname: string): string => {
  if (pathname.startsWith('/training')) return 'training';
  if (pathname.startsWith('/demo')) return 'demo';
  if (pathname.startsWith('/feedback')) return 'feedback';
  if (pathname.startsWith('/fallbackapp')) return 'fallbackapp';
  if (pathname.startsWith('/iam')) return 'iam';
  return 'app';
};

// Helper function to get storage key for application
const getStorageKey = (application: string): string => {
  return `thiranai-theme-${application}`;
};

// Load theme from localStorage for specific application
const loadThemeForApplication = (application: string): ThemeConfig => {
  const storageKey = getStorageKey(application);
  const savedTheme = localStorage.getItem(storageKey);

  if (savedTheme) {
    try {
      return JSON.parse(savedTheme);
    } catch (e) {
      console.error('Failed to parse saved theme:', e);
    }
  }

  // Return application-specific default
  return applicationDefaults[application] || applicationDefaults.app;
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const location = useLocation();
  const [currentApplication, setCurrentApplication] = useState<string>(() =>
    getApplicationFromPath(location.pathname)
  );

  const [themeConfig, setThemeConfig] = useState<ThemeConfig>(() => {
    const app = getApplicationFromPath(location.pathname);
    const config = loadThemeForApplication(app);

    // Apply CSS variables immediately on initialization
    const root = document.documentElement;
    root.style.setProperty('--primary-color', config.primaryColor);
    root.style.setProperty('--secondary-color', config.secondaryColor);
    document.body.style.setProperty('--primary-color', config.primaryColor);
    document.body.style.setProperty('--secondary-color', config.secondaryColor);

    return config;
  });

  // Detect application change from URL and load appropriate theme
  useEffect(() => {
    const newApplication = getApplicationFromPath(location.pathname);

    if (newApplication !== currentApplication) {
      setCurrentApplication(newApplication);
      const newTheme = loadThemeForApplication(newApplication);
      setThemeConfig(newTheme);

      // Debug logging
      if (typeof window !== 'undefined' && import.meta.env.DEV) {
        console.log('Application changed:', {
          from: currentApplication,
          to: newApplication,
          theme: newTheme
        });
      }
    }
  }, [location.pathname, currentApplication]);

  // Save theme to localStorage and apply CSS variables whenever it changes
  useEffect(() => {
    const storageKey = getStorageKey(currentApplication);
    localStorage.setItem(storageKey, JSON.stringify(themeConfig));

    // Apply CSS variables to document root for non-Ant Design components
    const root = document.documentElement;
    root.style.setProperty('--primary-color', themeConfig.primaryColor);
    root.style.setProperty('--secondary-color', themeConfig.secondaryColor);

    // Also apply to body for some legacy styles
    document.body.style.setProperty('--primary-color', themeConfig.primaryColor);
    document.body.style.setProperty('--secondary-color', themeConfig.secondaryColor);
  }, [themeConfig, currentApplication]);

  const updateTheme = (config: Partial<ThemeConfig>, application?: string) => {
    const targetApp = application || currentApplication;
    const storageKey = getStorageKey(targetApp);

    if (targetApp === currentApplication) {
      // Update current theme
      setThemeConfig(prev => ({ ...prev, ...config }));
    } else {
      // Update theme for different application (save to localStorage only)
      const currentThemeForApp = loadThemeForApplication(targetApp);
      const updatedTheme = { ...currentThemeForApp, ...config };
      localStorage.setItem(storageKey, JSON.stringify(updatedTheme));
    }
  };

  const resetTheme = (application?: string) => {
    const targetApp = application || currentApplication;
    const defaultTheme = applicationDefaults[targetApp] || applicationDefaults.app;

    if (targetApp === currentApplication) {
      setThemeConfig(defaultTheme);
    } else {
      const storageKey = getStorageKey(targetApp);
      localStorage.setItem(storageKey, JSON.stringify(defaultTheme));
    }
  };

  const getAntdTheme = () => {
    return {
      token: {
        // Primary colors
        colorPrimary: themeConfig.primaryColor,
        colorPrimaryBg: themeConfig.primaryColor + '1A', // 10% opacity
        colorPrimaryBgHover: themeConfig.primaryColor + '26', // 15% opacity
        colorPrimaryBorder: themeConfig.primaryColor + '40', // 25% opacity
        colorPrimaryBorderHover: themeConfig.primaryColor + '59', // 35% opacity
        colorPrimaryHover: themeConfig.primaryColor,
        colorPrimaryActive: themeConfig.primaryColor,

        // Secondary text color
        colorTextSecondary: themeConfig.secondaryColor,
      },
      components: {
        Button: {
          primaryColor: '#ffffff',
          primaryBg: themeConfig.primaryColor,
          primaryBorderColor: themeConfig.primaryColor,
          primaryHoverBg: themeConfig.primaryColor,
          primaryHoverBorderColor: themeConfig.primaryColor,
          colorPrimary: themeConfig.primaryColor,
          colorPrimaryHover: themeConfig.primaryColor,
          colorPrimaryActive: themeConfig.primaryColor,
        },
        Menu: {
          itemSelectedBg: themeConfig.primaryColor + '1A',
          itemSelectedColor: themeConfig.primaryColor,
        },
        Layout: {
          triggerBg: themeConfig.primaryColor,
          triggerColor: '#ffffff',
        },
      },
    };
  };

  const value: ThemeContextType = {
    themeConfig,
    currentApplication,
    updateTheme,
    resetTheme,
    getAntdTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
